



















import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import { IresDevice } from "@/data/models/IresDevice";
import { Vue, Component, Prop, Ref } from "vue-property-decorator";
import {
  IresDeviceRequest,
  IresDeviceRequestType,
} from "../../data/models/PanelRequest";
import {
  observeQueuedRequests,
  clearQueuedRequest,
} from "../../data/repository/PanelRequestRepository";

import { extractPayload } from "../../utils/panel/commands/FrameBuilder";

import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { getActiveUser } from "@/data/repository/UserRepository";
import { forceLoadRunsViaIotAccess } from "@/permissions/UserPermissions";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class QueuedRequestComponent extends Vue {
  /** Nothing may be changed while "Sicht auf" is active (ticket 2610051). */
  get isViewAsReadOnly() {
    return isViewAsSelected();
  }

  @Prop() iresDevice!: IresDevice;

  @Ref("confirmCancel")
  confirmationDialog!: ConfirmationDialogComponent;

  subscriptions: Array<() => void> = [];
  queuedPanelRequests: Array<IresDeviceRequest> = [];

  get activeUser() {
    return getActiveUser();
  }

  get isAllowedToForceLoadRuns() {
    return forceLoadRunsViaIotAccess(this.activeUser)
  }

  get latestQueuedPanelRequest() {
    const latestQueuedPanelRequest = this.queuedPanelRequests[0];

    if (latestQueuedPanelRequest?.requestType === IresDeviceRequestType.LOAD_RUNS) {
      return this.isAllowedToForceLoadRuns
        ? latestQueuedPanelRequest
        : null;
    }

    return latestQueuedPanelRequest;
  }

  get latestQueuedPanelRequestDescription() {
    const request = this.latestQueuedPanelRequest;

    if (!request) {
      return "";
    }

    switch (request.requestType) {
      // Both are a factory reset (command 102); DELETE_SAMPLES is its older, datalog-only form.
      case IresDeviceRequestType.COM_BOX_RESET:
      case IresDeviceRequestType.COM_BOX_DELETE_SAMPLES:
        return this.$t("panelControl.queuedRequestFactoryReset").toString();
      case IresDeviceRequestType.COM_BOX_RESUME:
        return this.$t("panelControl.queuedRequestResume").toString();
      case IresDeviceRequestType.COM_BOX_TERMINATE_DRY_RUN:
        return this.$t("panelControl.queuedRequestTerminate").toString();
      case IresDeviceRequestType.COM_BOX_PAUSE:
        return this.$t("panelControl.queuedRequestPause").toString();
      case IresDeviceRequestType.COM_BOX_TEST_RELAYS:
        return this.$t("panelControl.queuedRequestRelayTest").toString();
      case IresDeviceRequestType.COM_BOX_START_DRY_RUN:
        return this.$t("panelControl.queuedRequestPowerStartDryRun").toString();
      case IresDeviceRequestType.COM_BOX_CHANGE_DRY_RUN:
        return this.$t(
          "panelControl.queuedRequestPowerChangeDryRun"
        ).toString();
      case IresDeviceRequestType.COM_BOX_POWER_ON:
        return this.$t("panelControl.queuedRequestPowerOnComBox").toString();
      case IresDeviceRequestType.COM_BOX_POWER_OFF:
        return this.$t("panelControl.queuedRequestPowerOffComBox").toString();
      case IresDeviceRequestType.START_PROGRAM:
        return this.$t("panelControl.queuedRequestStartPanel").toString();
      case IresDeviceRequestType.STOP_PROGRAM:
        return this.$t("panelControl.queuedRequestStopPanel").toString();
      case IresDeviceRequestType.RESTART:
        return this.$t("panelControl.queuedRequestRestartPanel").toString();
      case IresDeviceRequestType.IOT_CONFIG:
        return this.$t("panelControl.queuedRequestUpdateIotConfig").toString();
      case IresDeviceRequestType.SELECT_PROGRAM: {
        const payload = extractPayload(request.payload);
        return this.$t("panelControl.queuedRequestSelectProgram", [
          payload[1],
        ]).toString();
      }
      case IresDeviceRequestType.LOAD_RUNS:
        return this.$t("panelControl.queuedRequestLoadRuns").toString();

      default:
        return "";
    }
  }

  async created() {
    this.subscriptions.push(
      observeQueuedRequests(
        this.iresDevice.serialNo,
        (queuedPanelRequests: Array<IresDeviceRequest>) => {
          this.queuedPanelRequests = queuedPanelRequests;
        }
      )
    );
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription();
    });
  }

  async onDeleteQueuedRequest() {
    const didConfrim = await this.confirmationDialog.show({
      title: this.$t("panelControl.confirmCancelQueuedRequestTitle").toString(),
      message: this.$t(
        "panelControl.confirmCancelQueuedRequestMessage"
      ).toString(),
    });

    if (didConfrim) {
      await this.deleteQueuedRequest();
    }
  }

  async deleteQueuedRequest() {
    const request = this.latestQueuedPanelRequest;

    if (!request) {
      return;
    }

    await clearQueuedRequest(this.iresDevice.serialNo, request.requestId);
  }
}
